import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { Clock, ChefHat, ArrowRight, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function KDS() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const tick = () => api.get("/kds/queue").then(r => setOrders(r.data)).catch(()=>{});
    tick();
    const t = setInterval(tick, 4000);
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, []);

  const advance = async (o) => {
    const next = o.status === "pending" ? "preparing" : "ready";
    try { await api.patch(`/orders/${o.id}/status`, { status: next }); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const fmt = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2,"0")}`;
  };

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BrandLogo size="md" className="text-white" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-sun bg-white/10 px-2 py-0.5 rounded-full">Kitchen Display</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-2xl font-heading font-extrabold tracking-tight">{new Date(now).toLocaleTimeString()}</div>
          {user && <button data-testid="kds-logout" onClick={() => { logout(); nav("/"); }} className="p-2 rounded-full hover:bg-white/10"><LogOut size={18} /></button>}
        </div>
      </header>

      <div className="p-6">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-white/40">
            <ChefHat size={64} className="mb-4" />
            <div className="font-heading text-3xl">All caught up</div>
            <div className="text-sm mt-2">No orders pending. Take a breath.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map(o => {
              const wait = (o.wait_seconds || 0) + Math.floor((now - (o._refreshed||now)) / 1000);
              const overdue = wait > 600;
              const newish = wait < 120;
              const cardKey = `${o.order_id}__${o.kot_id || ""}`;
              return (
                <div key={cardKey} data-testid={`kds-${cardKey}`}
                  className={`rounded-3xl p-5 border-2 transition-colors ${o.status === "preparing" ? 'bg-blue-500/10 border-blue-400' : overdue ? 'bg-red-500/10 border-red-400 animate-pulse' : 'bg-amber-500/10 border-amber-400'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-[0.2em] opacity-70 flex items-center gap-2">
                        {o.status}
                        {o.is_multi_vendor && <span className="px-1.5 py-0.5 rounded bg-sun text-ink text-[9px] font-extrabold">MULTI-VENDOR</span>}
                        {o.is_preorder && <span data-testid="kds-preorder-badge" className="px-1.5 py-0.5 rounded bg-emerald-400 text-ink text-[9px] font-extrabold">PREORDER · {o.scheduled_date}{o.meal_slot ? ` · ${o.meal_slot}` : ""}</span>}
                      </div>
                      <div className="font-heading text-2xl font-extrabold mt-1">{o.student_name || "Walk-in"}</div>
                      {o.student_grade && <div className="text-xs opacity-60">{o.student_grade}</div>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {o.vendor_name && <div className="text-xs px-2 py-0.5 rounded bg-white/10">{o.vendor_name}</div>}
                        {o.kot_id && <div className="text-[10px] px-2 py-0.5 rounded bg-brand text-white font-mono">KOT {o.kot_id}</div>}
                        {o.table_number && <div className="text-[10px] px-2 py-0.5 rounded bg-white/10 font-mono">T{o.table_number}</div>}
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-bold ${overdue ? 'text-red-300' : newish ? 'text-emerald-300' : 'text-white/70'}`}>
                      <Clock size={14} /> {fmt(wait)}
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5">
                    {o.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="font-semibold">{it.qty}× {it.name}</span>
                      </div>
                    ))}
                  </div>
                  {o.status !== "ready" && (
                    <button data-testid={`kds-advance-${cardKey}`} onClick={() => advance({ id: o.order_id, status: o.status })}
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white text-ink font-bold hover:bg-sun transition-colors">
                      {o.status === "pending" ? "Start preparing" : "Mark ready"} <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
